class ExpensesApi {
    
    /**
     * Get recommended chart of account for a vendor and state
     */
    getRecommendedChartAccount(vendorId, state) {
        const url = apiUrl + `expenses/vendors/${vendorId}/recommended-account?type=${state}`;
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Update expense state with optional account ID
     * Matches the backend: [HttpPut("state/{id}")]
     */
    updateState(data, accountId = null) {
        const url = apiUrl + `expenses/state/${data.id}${accountId ? `?accountId=${accountId}` : ''}`;
        return api.call('put', url, data)
            .then(({ data }) => data);
    }

    /**
     * Get all available expense status types
     * Matches the backend: [HttpGet("states")]
     */
    getStatusTypes() {
        const url = apiUrl + 'expenses/states';
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Quick approve expense (for list quick actions)
     * This would need to be implemented in the backend
     */
    quickApprove(expenseId, accountId = null) {
        // For now, use the existing updateState method
        return this.updateState({ id: expenseId, expenseState: 1 }, accountId);
    }

    /**
     * Mark expense as paid (for list quick actions)
     * This would need to be implemented in the backend
     */
    markAsPaid(expenseId, paymentDate = null, accountId = null) {
        // For now, use the existing updateState method
        return this.updateState({ id: expenseId, expenseState: 2 }, accountId);
    }

    /**
     * Remove invoice document from expense
     * Matches the backend: [HttpDelete("RemoveInvoiceDocument/{id}/{documentId}")]
     */
    removeInvoiceDocument(expenseId, documentId) {
        const url = apiUrl + `expenses/RemoveInvoiceDocument/${expenseId}/${documentId}`;
        return api.call('delete', url)
            .then(({ data }) => data);
    }

    /**
     * Remove payment document from expense
     * Matches the backend: [HttpDelete("RemovePaymentDocument/{id}/{documentId}")]
     */
    removePaymentDocument(expenseId, documentId) {
        const url = apiUrl + `expenses/RemovePaymentDocument/${expenseId}/${documentId}`;
        return api.call('delete', url)
            .then(({ data }) => data);
    }

    /**
     * Remove receipt document from expense
     * Matches the backend: [HttpDelete("RemoveReceiptDocument/{id}/{documentId}")]
     */
    removeReceiptDocument(expenseId, documentId) {
        const url = apiUrl + `expenses/RemoveReceiptDocument/${expenseId}/${documentId}`;
        return api.call('delete', url)
            .then(({ data }) => data);
    }

    /**
     * Get expense list for current business
     * Matches the backend: [HttpGet("currentBusiness")]
     */
    getListByCurrentBusiness(filters = {}) {
        let url = apiUrl + 'expenses/currentBusiness';
        
        // Add query parameters for filters if your backend supports them
        const queryParams = new URLSearchParams();
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
        if (filters.vendorId) queryParams.append('vendorId', filters.vendorId);
        if (filters.vendorName) queryParams.append('vendorName', filters.vendorName);
        if (filters.description) queryParams.append('description', filters.description);
        if (filters.amountMin) queryParams.append('amountMin', filters.amountMin);
        if (filters.amountMax) queryParams.append('amountMax', filters.amountMax);
        if (filters.page) queryParams.append('page', filters.page);
        if (filters.pageSize) queryParams.append('pageSize', filters.pageSize);
        if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
        if (filters.sortDirection) queryParams.append('sortDirection', filters.sortDirection);
        
        if (queryParams.toString()) {
            url += '?' + queryParams.toString();
        }
        
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Get all expenses (admin function)
     * Matches the backend: [HttpGet]
     */
    getList() {
        const url = apiUrl + 'expenses';
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Get single expense by ID
     * Matches the backend: [HttpGet("{id}")]
     */
    get(id) {
        const url = apiUrl + `expenses/${id}`;
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Get next expense in sequence (if implemented in backend)
     */
    getNext(id) {
        const url = apiUrl + `expenses/nextTo/${id}`;
        return api.call('get', url)
            .then(({ data }) => data);
    }
    
    /**
     * Get previous expense in sequence (if implemented in backend)
     */
    getPrev(id) {
        const url = apiUrl + `expenses/prevTo/${id}`;
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Create new expense
     * Matches the backend: [HttpPost]
     */
    create(data) {
        const url = apiUrl + 'expenses';
        return api.call('post', url, data)
            .then(({ data }) => data);
    }

    /**
     * Update existing expense
     * Matches the backend: [HttpPut("{id}")]
     */
    update(data) {
        const url = apiUrl + `expenses/${data.id}`;
        return api.call('put', url, data)
            .then(({ data }) => data);
    }

    /**
     * Delete expense
     * Matches the backend: [HttpDelete("{id}")]
     */
    delete(id) {
        const url = apiUrl + `expenses/${id}`;
        return api.call('delete', url)
            .then(({ data }) => data);
    }

    /**
     * Get expense statistics for dashboard
     */
    getStatistics(period = 'month') {
        const url = apiUrl + `expenses/statistics?period=${period}`;
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Bulk delete expenses
     */
    bulkDelete(expenseIds) {
        const url = apiUrl + 'expenses/bulk-delete';
        return api.call('post', url, { ids: expenseIds })
            .then(({ data }) => data);
    }

    /**
     * Bulk update expense status
     */
    bulkUpdateStatus(expenseIds, newStatus, accountId = null) {
        const url = apiUrl + 'expenses/bulk-update-status';
        const payload = {
            ids: expenseIds,
            status: newStatus,
            accountId
        };
        return api.call('post', url, payload)
            .then(({ data }) => data);
    }

    /**
     * Export expenses to CSV/Excel
     */
    export(filters = {}, format = 'csv') {
        let url = apiUrl + `expenses/export?format=${format}`;
        
        // Add filter parameters
        const queryParams = new URLSearchParams();
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
        if (filters.vendorId) queryParams.append('vendorId', filters.vendorId);
        
        if (queryParams.toString()) {
            url += '&' + queryParams.toString();
        }
        
        return api.call('get', url, {}, { responseType: 'blob' })
            .then(({ data }) => data);
    }

    /**
     * Duplicate expense
     */
    duplicate(id) {
        const url = apiUrl + `expenses/${id}/duplicate`;
        return api.call('post', url)
            .then(({ data }) => data);
    }

    /**
     * Add note/comment to expense
     */
    addNote(expenseId, note) {
        const url = apiUrl + `expenses/${expenseId}/notes`;
        return api.call('post', url, { note })
            .then(({ data }) => data);
    }

    /**
     * Get expense notes/comments
     */
    getNotes(expenseId) {
        const url = apiUrl + `expenses/${expenseId}/notes`;
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Upload document for expense
     */
    uploadDocument(expenseId, documentType, file) {
        const url = apiUrl + `expenses/${expenseId}/documents/${documentType}`;
        const formData = new FormData();
        formData.append('file', file);
        
        return api.call('post', url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then(({ data }) => data);
    }

    /**
     * Get expense document
     */
    getDocument(expenseId, documentType) {
        const url = apiUrl + `expenses/${expenseId}/documents/${documentType}`;
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Get expense audit trail
     */
    getAuditTrail(expenseId) {
        const url = apiUrl + `expenses/${expenseId}/audit-trail`;
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Search expenses with advanced filters
     */
    search(searchParams) {
        const url = apiUrl + 'expenses/search';
        return api.call('post', url, searchParams)
            .then(({ data }) => data);
    }

    /**
     * Get expense totals by period
     */
    getTotalsByPeriod(period = 'month', groupBy = 'vendor') {
        const url = apiUrl + `expenses/totals?period=${period}&groupBy=${groupBy}`;
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Get recurring expense templates
     */
    getRecurringTemplates() {
        const url = apiUrl + 'expenses/recurring-templates';
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Create expense from recurring template
     */
    createFromTemplate(templateId, overrides = {}) {
        const url = apiUrl + `expenses/recurring-templates/${templateId}/create`;
        return api.call('post', url, overrides)
            .then(({ data }) => data);
    }

    /**
     * Validate expense data
     */
    validate(expenseData) {
        const url = apiUrl + 'expenses/validate';
        return api.call('post', url, expenseData)
            .then(({ data }) => data);
    }

    /**
     * Get expense categories/tags
     */
    getCategories() {
        const url = apiUrl + 'expenses/categories';
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Create new expense category
     */
    createCategory(categoryData) {
        const url = apiUrl + 'expenses/categories';
        return api.call('post', url, categoryData)
            .then(({ data }) => data);
    }

    /**
     * Get expense approval workflow status
     */
    getApprovalWorkflow(expenseId) {
        const url = apiUrl + `expenses/${expenseId}/approval-workflow`;
        return api.call('get', url)
            .then(({ data }) => data);
    }

    /**
     * Submit expense for approval
     */
    submitForApproval(expenseId, approverIds = []) {
        const url = apiUrl + `expenses/${expenseId}/submit-approval`;
        return api.call('post', url, { approverIds })
            .then(({ data }) => data);
    }

    /**
     * Approve/Reject expense
     */
    processApproval(expenseId, action, comments = '', accountId = null) {
        const url = apiUrl + `expenses/${expenseId}/process-approval`;
        const payload = {
            action, // 'approve' or 'reject'
            comments,
            accountId
        };
        return api.call('post', url, payload)
            .then(({ data }) => data);
    }
     /**
     * Get top 5 most frequently used vendors for quick expense entry
     * Matches the backend: [HttpGet("quick-vendors")]
     */
     getQuickVendors() {
        const url = apiUrl + 'expenses/quick-vendors';
        return api.call('get', url)
            .then(({ data }) => data);
    }

    
    /**
     * Bulk create multiple expenses
     * Matches the backend: [HttpPost("bulk-create")]
     */
    bulkCreate(expenses) {
        const url = apiUrl + 'expenses/bulk-create';
        return api.call('post', url, { expenses })
            .then(({ data }) => data);
    }

}

export default ExpensesApi;